/**
 * Auto-ID generation (the "Auto ID generation — editor completion" TODO).
 *
 * Stable IDs must be **meaningless and permanent** (BEST-PRACTICES → *IDs must
 * be meaningless and permanent*): an ID never embeds a name/type/domain and
 * never changes. Hand-randomizing is genuinely hard, so the recommended path is
 * to let tooling mint one. This module is that tool — a truly random,
 * collision-checked ID, surfaced two ways:
 *
 *   - completion: type `#` in a declaration header → a freshly minted ID
 *     (`providers/completion.ts`).
 *   - code action: on any element lacking an ID, or in bulk for a whole file
 *     (`providers/code-actions.ts`).
 *
 * The generator is deliberately NOT in core/parser: those stay deterministic
 * (parser.ts never calls a non-deterministic generator). Randomness is an
 * editor-time concern, so it lives here.
 */

import { parse } from "@archlang/engine";
import type {
  ModuleDecl,
  ParsedFile,
  ProcessDecl,
  ResolvedModel,
  SourceSpan,
  SubprocessDecl,
  TestDecl,
  TopLevelDecl,
  TypeDecl,
  ViewDeclV2,
} from "@archlang/engine";

// Alphabet for minted IDs: lowercase base36 minus the visually ambiguous glyphs
// (`0`/`o`, `1`/`l`/`i`) so a hand-copied ID can't be silently mis-keyed. 31
// symbols — every char is a valid stable-id char (the lexer accepts alphanumeric
// suffixes after `#`).
const ID_ALPHABET = "23456789abcdefghjkmnpqrstuvwxyz";

// 31^6 ≈ 8.9e8 — collision-resistant for any realistic workspace, yet short
// enough to type around when reading source.
const ID_LENGTH = 6;

/** A random source returning a float in [0, 1). Injectable so tests are
 *  deterministic; production uses crypto-quality randomness. */
export type Rng = () => number;

const defaultRng: Rng = makeDefaultRng();

function makeDefaultRng(): Rng {
  // Prefer crypto-quality randomness; fall back to Math.random where the Web
  // Crypto API is absent (older runtimes). The ID is meaningless either way.
  const g = globalThis as {
    crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array };
  };
  const c = g.crypto;
  if (c?.getRandomValues) {
    const fill = c.getRandomValues.bind(c);
    return () => fill(new Uint32Array(1))[0]! / 0x1_0000_0000;
  }
  return Math.random;
}

function mint(rng: Rng): string {
  let s = "";
  for (let i = 0; i < ID_LENGTH; i++) {
    s += ID_ALPHABET[Math.floor(rng() * ID_ALPHABET.length)]!;
  }
  return s;
}

/**
 * A freshly minted, truly random stable ID (without the leading `#`) guaranteed
 * not to collide with anything in `taken`. Collision is astronomically unlikely
 * at this length, but checking is free and the contract is exact: the returned
 * ID is new. Under a pathological RNG (or a saturated `taken`, e.g. a tiny test
 * alphabet) it lengthens the ID rather than ever returning a duplicate.
 */
export function freshStableId(taken: ReadonlySet<string>, rng: Rng = defaultRng): string {
  for (let attempt = 0; attempt < 100; attempt++) {
    const id = mint(rng);
    if (!taken.has(id)) return id;
  }
  let id = mint(rng);
  while (taken.has(id)) id += ID_ALPHABET[Math.floor(rng() * ID_ALPHABET.length)]!;
  return id;
}

/**
 * A lazy per-request minter for surfaces that scaffold whole declarations
 * (completion snippets, Declare-module code actions): the first call builds
 * the workspace-wide taken set, every call returns a fresh unique id and
 * reserves it, so all ids minted within one request are mutually distinct AND
 * distinct from everything authored. Unaccepted scaffolds simply burn their
 * ids — ids are meaningless, the space is vast, and burning beats a save-time
 * mint the author may never run.
 */
export function makeIdMint(model: () => ResolvedModel, rng: Rng = defaultRng): () => string {
  let taken: Set<string> | undefined;
  return () => {
    taken ??= collectStableIds(model());
    const id = freshStableId(taken, rng);
    taken.add(id);
    return id;
  };
}

/**
 * Every authored stable ID in the workspace — the collision guard. Walks the
 * resolved model: modules (recursively through nested modules), types,
 * processes, subprocesses, and views (the ID-bearing elements; interfaces and
 * surfaces carry no stable ID). The model is workspace-wide (all files merged),
 * so this covers the whole package/space, not just the file being edited.
 */
export function collectStableIds(model: ResolvedModel): Set<string> {
  const out = new Set<string>();
  const add = (id?: string): void => {
    if (id) out.add(id);
  };
  const visit = (mods: ResolvedModel["modules"]): void => {
    for (const m of mods) {
      add(m.id);
      visit(m.modules);
    }
  };
  visit(model.modules);
  for (const t of model.types) add(t.id);
  for (const p of model.processes) add(p.id);
  for (const s of model.subprocesses) add(s.id);
  for (const v of model.views) add(v.id);
  return out;
}

// (Process-test ids are collected from the parsed file, not the resolved model —
// tests are not surfaced as a flat model array until the evaluator lands.)

// ---------------------------------------------------------------------------
// ID-target collection. The kind coverage below is the single source of truth
// for "which elements carry a stable id" — shared by the LSP "Generate stable
// IDs" code action (providers/code-actions.ts) and the formatter's
// mint-on-save (mintMissingIds, spec §3.1). Only modules, types, processes,
// subprocesses, and views bear ids; interfaces and surfaces do not. Unnamed
// modules and anonymous processes stay id-less until promoted.
// ---------------------------------------------------------------------------

export interface IdTarget {
  readonly name: string;
  readonly noun: string;
  /** Span at whose start `#<id> ` is inserted (the name, or the parent type
   *  for a `type` decl, since the id sits right after the `type` keyword). */
  readonly insert: SourceSpan;
  /** The whole declaration span — used to find the element under the cursor. */
  readonly span: SourceSpan;
}

/** Recursively gather ID-less, ID-bearing declarations from a top-level decl. */
export function collectIdTargets(top: TopLevelDecl, out: IdTarget[]): void {
  switch (top.kind) {
    case "type":
      collectType(top.decl, out);
      break;
    case "module":
      collectModule(top.decl, out);
      break;
    case "process":
      collectProcess(top.decl, out);
      break;
    case "subprocess":
      collectSubprocess(top.decl, out);
      break;
    case "view":
      collectView(top.decl, out);
      break;
    case "test":
      collectTest(top.decl, out);
      break;
  }
}

function collectType(t: TypeDecl, out: IdTarget[]): void {
  // A type's id sits after the `type` keyword, before the parent type. We don't
  // recurse into a type body — its members are template stamps, not instances.
  if (!t.id && t.parentTypeSource && t.source) {
    out.push({ name: t.name, noun: "type", insert: t.parentTypeSource, span: t.source });
  }
}

function collectModule(m: ModuleDecl, out: IdTarget[]): void {
  // Unnamed modules derive their name and stay id-less until promoted (§4.14).
  if (!m.id && !m.unnamed && m.nameSource && m.source) {
    out.push({ name: m.name, noun: "module", insert: m.nameSource, span: m.source });
  }
  for (const sub of m.body.modules) collectModule(sub, out);
  for (const p of m.body.processes) collectProcess(p, out);
  for (const sp of m.body.subprocesses) collectSubprocess(sp, out);
}

function collectProcess(p: ProcessDecl, out: IdTarget[]): void {
  // Anonymous (name-less) processes are a TODO until named — no id yet.
  if (!p.id && !p.anonymous && p.nameSource && p.source) {
    out.push({ name: p.name, noun: "process", insert: p.nameSource, span: p.source });
  }
  for (const sp of p.subprocesses ?? []) collectSubprocess(sp, out);
}

function collectSubprocess(sp: SubprocessDecl, out: IdTarget[]): void {
  if (!sp.id && sp.nameSource && sp.source) {
    out.push({ name: sp.name, noun: "subprocess", insert: sp.nameSource, span: sp.source });
  }
  for (const inner of sp.subprocesses ?? []) collectSubprocess(inner, out);
}

function collectView(v: ViewDeclV2, out: IdTarget[]): void {
  if (!v.id && v.nameSource && v.source) {
    out.push({ name: v.name, noun: "view", insert: v.nameSource, span: v.source });
  }
}

function collectTest(t: TestDecl, out: IdTarget[]): void {
  // A test's id sits after `test`, before the name string — inserted at the
  // name span's start, like a view's id sits before its name.
  if (!t.id && t.nameSource && t.source) {
    out.push({ name: t.name, noun: "test", insert: t.nameSource, span: t.source });
  }
}

// ---------------------------------------------------------------------------
// Mint-on-save (spec §3.1: "the formatter mints IDs on save"). Pure source →
// source: parse, find every ID-less bearer, and splice a freshly minted id in.
// Reuses the exact target coverage above and the exact `freshStableId`
// generator, so the ID shape is identical to the LSP code action.
// ---------------------------------------------------------------------------

export interface MintResult {
  /** Source with `#<id> ` spliced before every ID-less bearer (or `source`
   *  unchanged when there was nothing to mint). */
  readonly text: string;
  /** True iff at least one id was minted. */
  readonly changed: boolean;
  /** How many ids were minted. */
  readonly minted: number;
}

/**
 * Mint a fresh stable ID for every ID-less, ID-bearing element in `source`.
 * Splices happen right-to-left by offset so earlier insert points stay valid;
 * `taken` seeds from the ids already present in the file plus everything minted
 * in this pass, so a mint never collides. `rng` is injectable for deterministic
 * tests (production uses crypto-quality randomness).
 */
export function mintMissingIds(source: string, rng: Rng = defaultRng): MintResult {
  const { file } = parse(source);
  const targets: IdTarget[] = [];
  for (const top of file.declarations) collectIdTargets(top, targets);
  if (targets.length === 0) return { text: source, changed: false, minted: 0 };

  const taken = collectFileStableIds(file);
  const ordered = [...targets].sort(
    (a, b) => b.insert.start.offset - a.insert.start.offset
  );
  let text = source;
  for (const t of ordered) {
    const id = freshStableId(taken, rng);
    taken.add(id);
    const at = t.insert.start.offset;
    text = text.slice(0, at) + `#${id} ` + text.slice(at);
  }
  return { text, changed: true, minted: targets.length };
}

/** Stable ids already present in a parsed file — the mint collision guard.
 *  Mirrors {@link collectStableIds} but walks raw AST (no resolve needed). */
function collectFileStableIds(file: ParsedFile): Set<string> {
  const out = new Set<string>();
  const add = (id?: string): void => {
    if (id) out.add(id);
  };
  const visitModule = (m: ModuleDecl): void => {
    add(m.id);
    for (const sub of m.body.modules) visitModule(sub);
    for (const p of m.body.processes) visitProcess(p);
    for (const sp of m.body.subprocesses) visitSubprocess(sp);
  };
  const visitProcess = (p: ProcessDecl): void => {
    add(p.id);
    for (const sp of p.subprocesses ?? []) visitSubprocess(sp);
  };
  const visitSubprocess = (sp: SubprocessDecl): void => {
    add(sp.id);
    for (const inner of sp.subprocesses ?? []) visitSubprocess(inner);
  };
  for (const top of file.declarations) {
    switch (top.kind) {
      case "type":
        add(top.decl.id);
        break;
      case "module":
        visitModule(top.decl);
        break;
      case "process":
        visitProcess(top.decl);
        break;
      case "subprocess":
        visitSubprocess(top.decl);
        break;
      case "view":
        add(top.decl.id);
        break;
      case "test":
        add(top.decl.id);
        break;
    }
  }
  return out;
}
